/**
 * Meldet Client-Sackgassen an die Kundenakte des Backends
 * (ko-X-main: docs/plans/2026-09-06-kundenakte-client-sackgassen.md, Welle B).
 *
 * Drei Zustände, die der Server sonst nie sieht: leere Kursliste, gesperrter
 * Submit im Checkout, Checkout ohne Buchung verlassen. Der Server ordnet den
 * Bucher über den Login zu und reichert an (z. B. versteckte Kurse).
 *
 * Bewusst OHNE Apollo: Apollo-Mutations werden beim Unmount abgebrochen, ein
 * `fetch` mit `keepalive` überlebt Navigation und Tab-Wechsel. Der Header-Satz
 * kommt aus derselben Quelle wie der Apollo-Client (helpers/requestHeaders).
 *
 * Regeln: nur eingeloggt (der Server lehnt sonst ab, der Request wäre
 * Rauschen), einmal je Session und Dedupe-Schlüssel, Fehler werden
 * verschluckt — die Meldung darf den Buchungsflow nie stören.
 */
import { APOLLO_SERVER_URL } from "../constants/constants"
import { buildAuthHeaders, buildStaticHeaders } from "../helpers/requestHeaders"
import { safeSessionStorage, safeStorage } from "../helpers/safeStorage"

export type EndUserReportedEventType = "CourseListEmpty" | "CheckoutBlocked" | "CheckoutAbandoned"

export type EndUserEventStep = "CourseList" | "BookingSummary"

/** Spiegelt `CheckoutBlockerCode` des Backends (src/server/customerEvent/endUserEvent.types.ts). */
export type CheckoutBlockerCode =
    | "NoAttendeeFitsCourse"
    | "AllAttendeesAlreadyBooked"
    | "BookerHasNoAttendees"
    | "PaymentMethodMissing"
    | "AttendeeMissing"
    | "AttendeeAgeDoesNotFit"
    | "SepaDataInvalid"
    | "AttendeeCreationPending"
    | "RequirementGroupsUnmet"
    | "AgbUnconfirmed"
    | "PrivacyUnconfirmed"
    | "BookerBirthdayMissing"
    | "RequiredInputMissing"
    | "LateEntryGate"
    | "MollieMethodMissing"

/** Nur diese Filter darf der Client melden; `ids`/`restrictiveCourses` bleiben serverseitig. */
export type EndUserEventCourseFilter = {
    courseTypeId?: string | null
    courseTypeIds?: Array<string> | null
    locationId?: string | null
    city?: string | null
    dayFilter?: Array<string> | null
    prerequisiteCourseId?: string | null
    monthFilter?: Array<string> | null
    instructorId?: string | null
    courseCategoryId?: string | null
    courseCategoryIds?: Array<string> | null
}

export type EndUserEventInput = {
    type: EndUserReportedEventType
    step?: EndUserEventStep
    courseId?: string | null
    attendeeId?: string | null
    blockerCodes?: Array<CheckoutBlockerCode>
    hasPaymentMethod?: boolean | null
    filters?: EndUserEventCourseFilter
}

const REPORT_MUTATION = `mutation ReportEndUserEvent($input: EndUserEventInput!) {
  reportEndUserEvent(input: $input)
}`

const EMBED_CONVERSION_MUTATION = `mutation ReportEmbedConversion($reported: Boolean!) {
  reportEmbedConversion(reported: $reported)
}`

const DEDUPE_PREFIX = "ko:event:"

export const isLoggedIn = (): boolean => Boolean(safeStorage.getItem("loggedInUser"))

/**
 * Reduziert die Listenfilter auf die meldbaren Felder und lässt leere Werte
 * weg, damit gleiche Filter denselben Dedupe-Schlüssel ergeben.
 */
export const pickReportableFilters = (filters: EndUserEventCourseFilter | null | undefined): EndUserEventCourseFilter => {
    const result: EndUserEventCourseFilter = {}
    if (!filters) return result
    if (filters.courseTypeId) result.courseTypeId = filters.courseTypeId
    if (filters.courseTypeIds && filters.courseTypeIds.length > 0) result.courseTypeIds = [...filters.courseTypeIds].sort()
    if (filters.locationId) result.locationId = filters.locationId
    if (filters.city) result.city = filters.city
    if (filters.dayFilter && filters.dayFilter.length > 0) result.dayFilter = [...filters.dayFilter].sort()
    if (filters.prerequisiteCourseId) result.prerequisiteCourseId = filters.prerequisiteCourseId
    if (filters.monthFilter && filters.monthFilter.length > 0) result.monthFilter = [...filters.monthFilter].sort()
    if (filters.instructorId) result.instructorId = filters.instructorId
    if (filters.courseCategoryId) result.courseCategoryId = filters.courseCategoryId
    if (filters.courseCategoryIds && filters.courseCategoryIds.length > 0) result.courseCategoryIds = [...filters.courseCategoryIds].sort()
    return result
}

/**
 * Dedupe je Session: leere Liste je Filterkombination, blockierter Checkout je
 * Kurs und Blocker-Menge, Abbruch je Kurs.
 */
export const dedupeKeyFor = (input: EndUserEventInput): string => {
    switch (input.type) {
        case "CourseListEmpty":
            return `${DEDUPE_PREFIX}CourseListEmpty:${JSON.stringify(pickReportableFilters(input.filters))}`
        case "CheckoutBlocked":
            return `${DEDUPE_PREFIX}CheckoutBlocked:${input.courseId ?? ""}:${[...(input.blockerCodes ?? [])].sort().join(",")}`
        case "CheckoutAbandoned":
        default:
            return `${DEDUPE_PREFIX}${input.type}:${input.courseId ?? ""}`
    }
}

/** Fire-and-forget-POST mit denselben Headern wie der Apollo-Client; wirft nie. */
const postMutation = (operationName: string, query: string, variables: Record<string, unknown>): boolean => {
    if (!isLoggedIn()) return false
    if (!APOLLO_SERVER_URL || typeof fetch !== "function") return false
    try {
        fetch(APOLLO_SERVER_URL, {
            method: "POST",
            keepalive: true,
            credentials: "include",
            headers: {
                "content-type": "application/json",
                ...buildStaticHeaders(),
                ...buildAuthHeaders(),
            },
            body: JSON.stringify({ operationName, query, variables }),
        }).catch(() => undefined)
    } catch {
        // Meldungen dürfen den Buchungsflow nie stören.
    }
    return true
}

/**
 * Sendet die Meldung fire-and-forget. Liefert `true`, wenn ein Request
 * abgesetzt wurde (Tests), sonst `false` (nicht eingeloggt, Duplikat, kein
 * Server, kein fetch).
 */
export const reportEndUserEvent = (input: EndUserEventInput): boolean => {
    if (!isLoggedIn()) return false
    if (!APOLLO_SERVER_URL || typeof fetch !== "function") return false

    const key = dedupeKeyFor(input)
    if (safeSessionStorage.getItem(key)) return false
    safeSessionStorage.setItem(key, String(Date.now()))

    return postMutation("ReportEndUserEvent", REPORT_MUTATION, { input })
}

/**
 * Abdeckung der Conversion-Meldung (sc-3366): Nach jeder Buchung und jeder
 * Interessenten-Anmeldung meldet die App, ob `koConversion` an den
 * Parent-Wrapper ging. Daraus zählt das Backend je Mandant einen
 * Prometheus-Zähler. Kein Dedupe: jede Buchung zählt.
 */
export const reportEmbedConversion = (reported: boolean): boolean => postMutation("ReportEmbedConversion", EMBED_CONVERSION_MUTATION, { reported: Boolean(reported) })
